import { type FC, useDeferredValue, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '@components/Modal';
import { useEntityStore } from '@ContractBuilder/store';
import { batchCloneEndorsement } from '@mutations/batchCloneEndorsement';
import type { SubmissionShort } from '@queries/fetchSubmissions';
import { useSubmissionsOpensearch } from '@queries/fetchSubmissions';
import { filter } from 'lodash-es';

import { ToastContent } from '../components';
import type { CloneEndorsementModalProps } from '../types';
import { CloneEndorsementContent } from '../views';

export const CloneEndorsementModalOpenSearchController: FC<CloneEndorsementModalProps> = ({ handleClose }) => {
  'use memo';
  const [isCloning, setIsCloning] = useState(false);
  const { endorsementId, parentId } = useEntityStore(({ submission, endorsementParent }) => ({
    endorsementId: submission?.id,
    parentId: endorsementParent?.id,
  }));

  const [searchValue, setSearchValue] = useState('');

  const deferredSearchValue = useDeferredValue(searchValue);

  const {
    data: finalisedContractsPaginated,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSubmissionsOpensearch({
    search: deferredSearchValue,
    queryFilters: [{ field: 'status', comparator: 'eq', values: ['FINAL'] }],
  });

  const finalisedContracts = filter(
    finalisedContractsPaginated?.pages.flatMap((page) => page.data) ?? [],
    ({ id }) => id !== parentId,
  );

  const handleApply = async (contractIds: string[]) => {
    if (!parentId || !endorsementId) {
      toast.error(<ToastContent kind="error" />);
      return;
    }

    try {
      setIsCloning(true);
      await batchCloneEndorsement({
        submissionId: parentId,
        endorsementId,
        contractIds,
      });

      toast.success(<ToastContent kind="success" />);
      return handleClose();
    } catch (error) {
      console.error('Error cloning endorsement', error);
      toast.error(<ToastContent kind="error" />);
    } finally {
      setIsCloning(false);
    }
  };

  const [selectedContracts, setSelectedContracts] = useState<SubmissionShort[]>([]);
  const selectedCount = selectedContracts.length;

  const addContract = (contract: SubmissionShort) => {
    setSelectedContracts((previousContracts) => [...previousContracts, contract]);
  };

  const removeContract = (contractId: string) => {
    setSelectedContracts((previousContracts) => previousContracts.filter((contract) => contract.id !== contractId));
  };

  const notSelectedContracts = finalisedContracts.filter(
    ({ id }) => !selectedContracts.some((contract) => contract.id === id),
  );

  return (
    <Modal
      className="gap-0 sm:max-w-[1024px]!"
      onClose={handleClose}
      open
      shouldShowCloseIcon
      title="Duplicate Endorsement to Multiple Contracts"
      titleClassName="justify-start!"
    >
      <CloneEndorsementContent
        filteredContracts={notSelectedContracts}
        isCloning={isCloning}
        isLoading={isLoading}
        onCancel={handleClose}
        onApply={handleApply}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedContracts={selectedContracts}
        selectedCount={selectedCount}
        addContract={addContract}
        removeContract={removeContract}
      />
    </Modal>
  );
};
